import { createElement, lazy, type ReactElement, Suspense } from "react"
import { Outlet, type RouteObject } from "react-router"

import { getAppLayout, type NextPageWithLayout } from "@/common/app"
// TYPE-ONLY on purpose. `ROLES_BASE` is only ever read here as `typeof`, and a
// value import would pull `role-scope-route` — and the Zod schema it holds —
// into the eager entry chunk for a string this file never evaluates.
import type { ROLES_BASE } from "@/common/role-scope/role-scope-route"
import { adminRoutes } from "@/modules/Admin/router"

// The shared chrome is lazy so it stays out of the entry chunk — it loads once
// on the first authed page and then persists (the layout routes below keep it
// mounted across navigation).
const MainLayout = lazy(() => import("@/common/layouts/MainLayout"))

/**
 * Explicit route table for the Vite/React Router SPA (NEX-248 Phase C).
 *
 * Every page under src/pages is listed as a literal `path -> dynamic import`
 * pair. Authored once from the Next filename convention ([id] -> :id), then
 * frozen: no filesystem globbing, no runtime path parsing. Search a URL to find
 * its page; add a page by adding a line. React Router ranks by specificity, so
 * order here is for humans, not matching.
 *
 * **Persistent layouts.** Under Next, `_app` rendered `getLayout(<Page/>)` at a
 * stable position, so the shared `MainLayout` (nav + sidebar) instance survived
 * navigation. React Router needs that expressed structurally: the 88 pages that
 * shared `MainLayout` are mounted as children of persistent layout routes that
 * render the chrome once around an `<Outlet/>`. Without this the layout (and the nav) would
 * unmount and remount on every navigation. Those pages render BARE here (via
 * `bare()`); their per-page `getLayout` static is now unused. Standalone pages
 * (sign-in, embed, oauth callbacks, full-screen flows) keep `route()`, which
 * still applies their own `getLayout` (or none).
 *
 * Catch-all pages ([...x] / [[...x]]) become a `*` splat plus a `splat` arg
 * naming Next's param. React Router only exposes the remainder under "*", so
 * the navigation adapter reads `handle.splat` to re-publish it under the name
 * the app expects (router.query.slug / params / path) as a string[] — matching
 * Next's catch-all shape. Without this, deep links lose their params.
 */

type PageModule = { default: NextPageWithLayout }

/** Lazy page that applies its own `getLayout` — for standalone routes that bring
 *  their own chrome (or none). */
function pageElement(importer: () => Promise<PageModule>): ReactElement {
  const Page = lazy(async () => {
    const { default: Component } = await importer()
    const WithLayout = () => getAppLayout(Component, createElement(Component)) as ReactElement
    return { default: WithLayout }
  })
  return (
    <Suspense fallback={null}>
      <Page />
    </Suspense>
  )
}

/** Lazy page rendered BARE — the chrome comes from a persistent parent layout
 *  route, so the layout mounts once and survives navigation. The Suspense sits
 *  inside the layout, so the nav stays visible while a page chunk loads. */
function bareElement(importer: () => Promise<PageModule>): ReactElement {
  const Page = lazy(async () => ({ default: (await importer()).default }))
  return (
    <Suspense fallback={null}>
      <Page />
    </Suspense>
  )
}

function route(path: string, importer: () => Promise<PageModule>, splat?: string): RouteObject {
  return { path, element: pageElement(importer), handle: splat ? { splat } : undefined }
}

function bare(path: string, importer: () => Promise<PageModule>, splat?: string): RouteObject {
  return { path, element: bareElement(importer), handle: splat ? { splat } : undefined }
}

/** Every screen the Roles surface ships. Closed — a sixth needs a member here. */
type RoleSurfaceScreen = "register" | "systemMap" | "unassigned" | "agentDetail" | "roleDetail"

/** Route patterns are relative here; `ROLES_BASE` is an app URL. */
type WithoutLeadingSlash<T extends string> = T extends `/${infer Rest}` ? Rest : T

/**
 * The Roles surface: ONE route per screen, asserted rather than agreed.
 *
 * 🚨 **THIS IS WHY "the register has two URLs" CANNOT COME BACK BY ACCIDENT.**
 * It was true until now — `app/roles` and a bare `app/overview` both rendered
 * `RolesIndexView`, and the only thing objecting was a comment in each page
 * module asking the reader to keep them matched.
 *
 * Three properties, all from the type and none from care:
 *
 * 1. **One path per screen.** These are object KEYS, so `register` cannot be
 *    written twice — TypeScript refuses a duplicate key outright.
 * 2. **No unlisted screen.** `satisfies Record<RoleSurfaceScreen, string>` is
 *    exhaustive in both directions: a sixth Roles route with no member is an
 *    excess property, and a member with no route is a missing one.
 * 3. **The register's pattern IS the canonical URL.** `register` is typed as
 *    `ROLES_BASE` with its leading slash removed, so the two spellings cannot
 *    drift — editing either without the other stops compiling.
 *
 * What it deliberately does NOT do: stop someone registering a sixth route by
 * hand elsewhere in this table. No type reaches that. What it does is make the
 * Roles surface a closed set that a reviewer can see the whole of in one place.
 */
const ROLE_SURFACE_PATHS = {
  register: "app/roles",
  systemMap: "app/overview/*",
  unassigned: "app/roles/unassigned",
  agentDetail: "app/roles/agent/:agent-id",
  roleDetail: "app/roles/role/:role-id"
} as const satisfies Record<RoleSurfaceScreen, string> & {
  register: WithoutLeadingSlash<typeof ROLES_BASE>
}

/** Persistent customer app shell — mounts MainLayout once around the page Outlet. */
function MainLayoutShell(): ReactElement {
  return (
    <Suspense fallback={null}>
      <MainLayout>
        <Outlet />
      </MainLayout>
    </Suspense>
  )
}

export const pageRoutes: RouteObject[] = [
  // Admin panel — its own AdminLayout subtree, hosted in this one router.
  ...adminRoutes,

  // Persistent customer app shell: MainLayout mounts once; pages swap in the Outlet.
  {
    element: createElement(MainLayoutShell),
    children: [
      bare(
        "app/agent-creation/*",
        () => import("./pages/app/agent-creation/[[...params]]"),
        "params"
      ),
      bare(
        "app/ai-task-creation/*",
        () => import("./pages/app/ai-task-creation/[[...params]]"),
        "params"
      ),
      bare("app/analytics", () => import("./pages/app/analytics/index")),
      bare("app/analytics-reports", () => import("./pages/app/analytics-reports/index")),
      bare("app/bug-report", () => import("./pages/app/bug-report/index")),
      bare("app/chats/:chat-id", () => import("./pages/app/chats/[chat-id]")),
      bare("app/connect-waba", () => import("./pages/app/connect-waba")),
      bare("app/contacts", () => import("./pages/app/contacts/index")),
      bare("app/contacts/:id", () => import("./pages/app/contacts/[id]")),
      bare("app/dashboard", () => import("./pages/app/dashboard/index")),
      bare("app/deployments", () => import("./pages/app/deployments/index")),
      bare("app/deployments/:id", () => import("./pages/app/deployments/[id]/index")),
      bare(
        "app/deployments/add-airtable-deployment",
        () => import("./pages/app/deployments/add-airtable-deployment")
      ),
      bare(
        "app/deployments/add-api-deployment",
        () => import("./pages/app/deployments/add-api-deployment")
      ),
      bare(
        "app/deployments/add-email-deployment",
        () => import("./pages/app/deployments/add-email-deployment")
      ),
      bare(
        "app/deployments/add-embed-deployment",
        () => import("./pages/app/deployments/add-embed-deployment")
      ),
      bare(
        "app/deployments/add-google-meet-deployment",
        () => import("./pages/app/deployments/add-google-meet-deployment")
      ),
      bare(
        "app/deployments/add-google-sheet-deployment",
        () => import("./pages/app/deployments/add-google-sheet-deployment")
      ),
      bare(
        "app/deployments/add-slack-deployment",
        () => import("./pages/app/deployments/add-slack-deployment")
      ),
      bare(
        "app/deployments/add-teams-deployment",
        () => import("./pages/app/deployments/add-teams-deployment")
      ),
      bare(
        "app/deployments/add-telegram-deployment",
        () => import("./pages/app/deployments/add-telegram-deployment")
      ),
      bare(
        "app/deployments/add-whatsapp-deployment",
        () => import("./pages/app/deployments/add-whatsapp-deployment")
      ),
      bare(
        "app/deployments/add-zoom-deployment",
        () => import("./pages/app/deployments/add-zoom-deployment")
      ),
      bare("app/deployments/excel", () => import("./pages/app/deployments/excel")),
      bare("app/deployments/google-sheet", () => import("./pages/app/deployments/google-sheet")),
      bare(
        "app/deployments/outlook-add-in",
        () => import("./pages/app/deployments/outlook-add-in")
      ),
      bare("app/deployments/power-point", () => import("./pages/app/deployments/power-point")),
      bare("app/deployments/word", () => import("./pages/app/deployments/word")),
      bare("app/dev/component-test", () => import("./pages/app/dev/component-test")),
      bare("app/document-templates", () => import("./pages/app/document-templates/index")),
      bare("app/document-templates/:id", () => import("./pages/app/document-templates/[id]")),
      bare(
        "app/document-templates/excel-template",
        () => import("./pages/app/document-templates/excel-template")
      ),
      bare(
        "app/document-templates/power-point-template",
        () => import("./pages/app/document-templates/power-point-template")
      ),
      bare(
        "app/document-templates/power-point-template-split",
        () => import("./pages/app/document-templates/power-point-template-split")
      ),
      bare(
        "app/document-templates/word-template",
        () => import("./pages/app/document-templates/word-template")
      ),
      bare("app/documents", () => import("./pages/app/documents/index")),
      bare("app/documents/folder/:id", () => import("./pages/app/documents/folder/[id]")),
      bare("app/emulator", () => import("./pages/app/emulator/index")),
      bare("app/inbox/*", () => import("./pages/app/inbox/[[...slug]]"), "slug"),
      bare("app/integrations", () => import("./pages/app/integrations/index")),
      bare(
        "app/integrations/add-airtable-integration",
        () => import("./pages/app/integrations/add-airtable-integration")
      ),
      bare(
        "app/integrations/add-api-integration",
        () => import("./pages/app/integrations/add-api-integration")
      ),
      bare(
        "app/integrations/add-embed-integration",
        () => import("./pages/app/integrations/add-embed-integration")
      ),
      bare(
        "app/integrations/add-google-meet-integration",
        () => import("./pages/app/integrations/add-google-meet-integration")
      ),
      bare(
        "app/integrations/add-google-sheet-integration",
        () => import("./pages/app/integrations/add-google-sheet-integration")
      ),
      bare(
        "app/integrations/add-slack-integration",
        () => import("./pages/app/integrations/add-slack-integration")
      ),
      bare(
        "app/integrations/add-teams-integration",
        () => import("./pages/app/integrations/add-teams-integration")
      ),
      bare(
        "app/integrations/add-whatsapp-integration",
        () => import("./pages/app/integrations/add-whatsapp-integration")
      ),
      bare(
        "app/integrations/add-zoom-integration",
        () => import("./pages/app/integrations/add-zoom-integration")
      ),
      bare("app/integrations/excel", () => import("./pages/app/integrations/excel")),
      bare("app/integrations/google-sheet", () => import("./pages/app/integrations/google-sheet")),
      bare(
        "app/integrations/outlook-add-in",
        () => import("./pages/app/integrations/outlook-add-in")
      ),
      bare("app/integrations/power-point", () => import("./pages/app/integrations/power-point")),
      bare("app/integrations/word", () => import("./pages/app/integrations/word")),
      bare("app/invitations", () => import("./pages/app/invitations")),
      bare("app/my-agents", () => import("./pages/app/my-agents/index")),
      bare(
        "app/my-agents/:agent-id/skills/:skill-id/edit",
        () => import("./pages/app/my-agents/[agent-id]/skills/[skill-id]/edit")
      ),
      bare(
        "app/my-agents/:agent-id/tabs/:tab-key",
        () => import("./pages/app/my-agents/[agent-id]/tabs/[tab-key]/index")
      ),
      bare("app/my-ai-tasks/:id", () => import("./pages/app/my-ai-tasks/[id]/index")),
      bare("app/my-tools", () => import("./pages/app/my-tools/index")),
      bare("app/my-tools/:id", () => import("./pages/app/my-tools/[id]")),
      bare("app/my-tools/:id/evaluate", () => import("./pages/app/my-tools/[id]/evaluate/index")),
      bare("app/my-tools/executions", () => import("./pages/app/my-tools/executions/index")),
      // Above "app/my-tools/:id" for a human reader only — React Router ranks a
      // static segment over a dynamic one regardless of order here, so
      // "/app/my-tools/mcp-servers" cannot be captured as a tool id.
      bare("app/my-tools/mcp-servers", () => import("./pages/app/my-tools/mcp-servers/index")),
      bare("app/my-tools/mcp-servers/:id", () => import("./pages/app/my-tools/mcp-servers/[id]")),
      // Top-level, not under "app/settings" — the org system map is a product
      // destination now, not a Roles administration tab.
      //
      // ONE route for both the org overview and a Role's, matching the split
      // view's reason rather than copying its shape: two `bare()` entries would
      // build two `lazy()` components, so React Router would treat them as
      // different elements and REMOUNT the whole map every time the scope
      // changed. A splat's remainder is optional, so this pattern matches
      // "/app/overview" as well.
      //
      // No splat NAME on purpose. The remainder is the role scope and it is read
      // from the pathname by `RoleScopeProvider`, which sits above the route
      // match and above `QueryClientProvider`; republishing it into
      // `router.query` as a string[] would be a second way to ask the same
      // question, and the second way is the one that goes stale.
      bare(ROLE_SURFACE_PATHS.systemMap, () => import("./pages/app/overview/index")),
      // THE register, at its one URL. The splat above still matches a bare
      // "/app/overview" — that page redirects here rather than rendering a
      // second copy of this screen.
      bare(ROLE_SURFACE_PATHS.register, () => import("./pages/app/roles/index")),
      // The orphan backlog. It moved OUT of Settings with the rest of the Roles
      // administration surface and is a sibling of the index, not a child of it:
      // both are static segments under "/app/roles", so neither shadows the
      // other and neither is a ":id" route.
      bare(ROLE_SURFACE_PATHS.unassigned, () => import("./pages/app/roles/unassigned")),
      // One agent, in full — the design's `adeep` surface, reached from the
      // system map's agent drawer.
      //
      // NOT under "app/overview/*": that entry is a splat whose remainder IS
      // the role scope, so "/app/overview/agent/<id>" would resolve "agent" as
      // a role id. "app/roles" above is an exact path with nothing beneath it.
      // It cannot collide with "app/roles/unassigned" either — that is ONE
      // static segment under /app/roles and this is two, so no path matches
      // both patterns. ":agent-id" matches "app/my-agents/:agent-id/...", the
      // only other agent-keyed route.
      bare(
        ROLE_SURFACE_PATHS.agentDetail,
        () => import("./pages/app/roles/agent/[agent-id]/index")
      ),
      // One Role, in full. `RoleDetailPane` was mounted by the Settings split
      // view and by nothing else; that shell is gone and this is its home.
      //
      // The segment is `role` for the same reason `agent` above is a segment:
      // "app/roles" is an exact path with nothing beneath it, so a two-segment
      // suffix under it shadows no route and is shadowed by none. It cannot
      // collide with "app/roles/unassigned" (ONE static segment, not two), and
      // "role" is a different literal from "agent".
      //
      // Deliberately NOT a bare :role-id directly under app/roles. React Router
      // would rank the static "unassigned" above it today, but the two entries
      // above document that nothing under /app/roles is a ":id" route, and a
      // property held by ranking alone is one the next static sibling gets to
      // rediscover.
      //
      // 🚨 That rejected pattern is described here rather than SPELLED, because
      // `buildRoleDetailPath.test.ts` and its sibling ask this file "do you
      // carry this quoted pattern" — a quoted example in a comment answers yes
      // for a route that is not registered, which is a green a reader cannot
      // tell from a real one.
      // `buildRoleDetailPath` is the one spelling of this URL, and its spec
      // asserts the pattern against this table in both directions.
      bare(ROLE_SURFACE_PATHS.roleDetail, () => import("./pages/app/roles/role/[role-id]/index")),
      bare(
        "app/settings/access-cards/*",
        () => import("./pages/app/settings/access-cards/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/api-keys/*",
        () => import("./pages/app/settings/api-keys/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/audit-logs/*",
        () => import("./pages/app/settings/audit-logs/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/billing/*",
        () => import("./pages/app/settings/billing/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/contacts/*",
        () => import("./pages/app/settings/contacts/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/credentials/*",
        () => import("./pages/app/settings/credentials/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/data-retention",
        () => import("./pages/app/settings/data-retention/index")
      ),
      bare(
        "app/settings/developer/*",
        () => import("./pages/app/settings/developer/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/general/*",
        () => import("./pages/app/settings/general/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/macros/*",
        () => import("./pages/app/settings/macros/[[...index]]"),
        "index"
      ),
      bare("app/settings/memory", () => import("./pages/app/settings/memory/index")),
      bare("app/settings/models", () => import("./pages/app/settings/models/index")),
      bare(
        "app/settings/notifications/*",
        () => import("./pages/app/settings/notifications/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/organization-list/*",
        () => import("./pages/app/settings/organization-list/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/permissions/*",
        () => import("./pages/app/settings/permissions/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/phone-number/*",
        () => import("./pages/app/settings/phone-number/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/profile/*",
        () => import("./pages/app/settings/profile/[[...index]]"),
        "index"
      ),
      // Settings, scoped to the Role in the global role scope.
      //
      // 🚨 NEITHER PATH NAMES THE ROLE, AND THAT IS THE DESIGN. These are
      // `silent` routes in `role-scope-route.ts`'s sense — they declare no
      // scope, so the stored selection stands. A `:role-id` segment here would
      // create a SECOND scope-declaring route family, which is exactly the
      // "two stores of one value" that module exists to make unrepresentable.
      // `RoleSettingsPane`'s docblock carries the full argument.
      //
      // Two `bare()` entries rather than one splat: these are two different
      // pages with two different bodies, not one page reading a remainder, and
      // the rail lights a different row for each.
      // ORG-scoped, so it is `role-governance` and NOT a fourth page under
      // `app/settings/role/`. That family is the Role-scoped one — its rows all
      // declare `requiresRoleScope` and read the global role scope — and this
      // screen has no Role subject at all: `RoleManagementController` is
      // org-admin-only with the organisation taken from the session.
      //
      // `role-governance` is a distinct literal segment from `role`, so it
      // shadows nothing under it and nothing under it shadows this.
      bare(
        "app/settings/role-governance",
        () => import("./pages/app/settings/role-governance/index")
      ),
      bare("app/settings/role/general", () => import("./pages/app/settings/role/general/index")),
      bare("app/settings/role/people", () => import("./pages/app/settings/role/people/index")),
      bare("app/settings/role/cue", () => import("./pages/app/settings/role/cue/index")),
      bare(
        "app/settings/tickets/*",
        () => import("./pages/app/settings/tickets/[[...index]]"),
        "index"
      ),
      bare("app/settings/tracks", () => import("./pages/app/settings/tracks/index")),
      bare(
        "app/settings/user-groups/*",
        () => import("./pages/app/settings/user-groups/[[...index]]"),
        "index"
      ),
      bare(
        "app/settings/vault/*",
        () => import("./pages/app/settings/vault/[[...index]]"),
        "index"
      ),
      bare("app/test-chat", () => import("./pages/app/test-chat/index")),
      bare("app/tracks", () => import("./pages/app/tracks/index")),
      bare("app/triggers", () => import("./pages/app/triggers/index")),
      bare("app/user-profile", () => import("./pages/app/user-profile/index")),
      bare("app/vibe", () => import("./pages/app/vibe/index")),
      bare("app/vibe/approvals", () => import("./pages/app/vibe/approvals/index")),
      bare("app/vibe/usage", () => import("./pages/app/vibe/usage/index")),
      bare("app/vibe/:appId", () => import("./pages/app/vibe/[appId]/index")),
      bare("app/workflows", () => import("./pages/app/workflows/index")),
      bare("app/workflows/:id", () => import("./pages/app/workflows/[id]")),
      bare("app/workspaces", () => import("./pages/app/workspaces/index")),
      bare("app/workspaces/:slug", () => import("./pages/app/workspaces/[slug]/index")),
      bare(
        "app/workspaces/:slug/*",
        () => import("./pages/app/workspaces/[slug]/[...path]"),
        "path"
      )
    ]
  },

  // Standalone pages — full-screen flows / redirects that bring their own layout
  // (or none); no shared persistent chrome.
  route("", () => import("./pages/index")),
  route("app", () => import("./pages/app/index")),
  route("app/chats", () => import("./pages/app/chats/index")),
  route(
    "app/create-organization/*",
    () => import("./pages/app/create-organization/[[...params]]"),
    "params"
  ),
  route("app/documents/google-callback", () => import("./pages/app/documents/google-callback")),
  route("app/documents/notion-callback", () => import("./pages/app/documents/notion-callback")),
  route("app/documents/oauth-error", () => import("./pages/app/documents/oauth-error")),
  route("app/documents/oauth-success", () => import("./pages/app/documents/oauth-success")),
  route(
    "app/documents/sharepoint-callback",
    () => import("./pages/app/documents/sharepoint-callback")
  ),
  route("app/handshake/callback", () => import("./pages/app/handshake/callback")),
  route("app/my-tools/create-tool", () => import("./pages/app/my-tools/create-tool/index")),
  route(
    "app/my-tools/oauth/callback/:tool-id",
    () => import("./pages/app/my-tools/oauth/callback/[tool-id]/index")
  ),
  route(
    "app/my-tools/oauth2-credentials/callback/:tool-id",
    () => import("./pages/app/my-tools/oauth2-credentials/callback/[tool-id]")
  ),
  route(
    "app/my-tools/mcp-servers/oauth/callback",
    () => import("./pages/app/my-tools/mcp-servers/oauth/callback")
  ),
  route("app/oauth/callback", () => import("./pages/app/oauth/callback")),
  route("app/oauth/error", () => import("./pages/app/oauth/error")),
  route("app/onboarding", () => import("./pages/app/onboarding/index")),
  route("app/settings", () => import("./pages/app/settings/index")),
  // Docs is client-rendered: the catch-all fetches the pre-serialized page from
  // the backend; splat name "slug" matches what the docs page reads.
  route("docs/*", () => import("@/modules/Docs/DocsSpaPage"), "slug"),
  route("embed/:embed-id", () => import("./pages/embed/[embed-id]")),
  route("payment-failed", () => import("./pages/payment-failed")),
  route("payment-success", () => import("./pages/payment-success")),
  route("select-organization", () => import("./pages/select-organization")),
  route("sign-in/*", () => import("./pages/sign-in/[[...signin]]"), "signin"),
  route("sign-up/*", () => import("./pages/sign-up/[[...signup]]"), "signup"),
  // The Vibe app sign-in handshake. Standalone rather than under the console
  // layout: it is a step in a redirect chain into a tenant's own app, and the
  // nav chrome would flash for the fraction of a second it renders.
  route("vibe/authorize", () => import("./pages/vibe/authorize/index")),
  // The auth.md claim ceremony (NEX-4203). Standalone for the same reason: the
  // person arrives from an agent to answer one question and leave. `/agent-auth/`
  // is in PROTECTED_PREFIXES, so reaching it signed-out bounces to sign-in and
  // returns with the claim-attempt token intact.
  route("agent-auth/claim", () => import("./pages/agent-auth/claim")),
  // `nexus login`'s two approval pages. Standalone for the same reason, and `/cli/` is
  // in PROTECTED_PREFIXES for the same reason. `vercel.json` refuses to frame them.
  route("cli/authorize/:sessionId", () => import("./pages/cli/authorize/[sessionId]/index")),
  route("cli/device", () => import("./pages/cli/device/index")),
  route("test-embed", () => import("./pages/test-embed")),
  // Unmatched-route fallback — renders the existing 404 page. Lowest specificity.
  route("*", () => import("./pages/404"))
]
